/**
 * Service de traduction via le backend (évite CORS)
 * Le backend appelle Google → MyMemory → LibreTranslate
 * Support : Français (fr), Khmer (km), Anglais (en)
 */

import apiConfig from "../config/api.js";

// Cache pour éviter de traduire le même texte plusieurs fois
const translationCache = new Map();

const getTranslateUrl = () => `${apiConfig.baseURL}/translate`;

/** Fautes courantes → orthographe correcte (pour que l’API comprenne même si c’est mal écrit) */
const NORMALIZE_SPELLING = [
  ["soucre", "sucre"],
  ["sucree", "sucre"],
  ["cacherole", "casserole"],
  ["cacheroles", "casseroles"],
  ["cuillere", "cuillère"],
  ["cuilleres", "cuillères"],
  ["gouse", "gousse"],
  ["aumoins", "au moins"],
  ["melanger", "mélanger"],
  ["melange", "mélange"],
  ["creme", "crème"],
  ["framge", "fromage"],
  ["facil", "facile"],
  ["rapé", "râpé"],
  ["rape", "râpé"],
  ["dongueux", "râpé"],
  ["poid", "poids"],
  ["pate", "pâte"],
  ["pates", "pâtes"],
];

/** Corrige les fautes courantes dans le texte avant envoi à l’API */
const normalizeMisspellings = (text) => {
  if (!text || typeof text !== "string") return text;
  let t = text.trim();
  for (const [wrong, correct] of NORMALIZE_SPELLING) {
    const re = new RegExp(
      `\\b${wrong.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "gi",
    );
    t = t.replace(re, correct);
  }
  return t;
};

/** Détecte si le texte est probablement en français (accents ou mots courants, même mal écrits) */
const looksLikeFrench = (text) => {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  if (/[àâäéèêëïîôùûüÿæœç]/i.test(t)) return true;
  const frenchWords =
    /\b(le|la|les|un|une|des|du|de|dans|sur|pour|avec|sans|mettre|faire|prendre|il faut|etre|avoir|sel|poivre|sucre|soucre|pates|eau|farine|beurre|oeuf|oeufs|chaud|min|mn|cuire|melanger|ajouter|servir|cacherole|cuillere|tasse|grammes|minutes|aumoins|au moins|gousses|ail|gousse|gouse|tomates|oignon|carottes|pommes|creme|fromage|viande|poulet|boeuf|porc|poisson|herbes|basilic|persil|thym|laurier|huile|vinaigre|sauce|bouillon|piment|paprika|cumin|cannelle|noix|amandes|raisin|citron|orange|lait|yaourt)\b/i;
  return frenchWords.test(t);
};

/**
 * Traduction via le proxy backend (pas d'appel direct aux APIs externes = pas de CORS)
 * Si sourceLang = "auto" et que le texte ressemble au français, on envoie "fr" pour que la traduction fonctionne.
 */
export const translateTextAPI = async (
  text,
  targetLang = "km",
  sourceLang = "auto",
) => {
  const effectiveSource =
    sourceLang === "auto" && looksLikeFrench(text) ? "fr" : sourceLang;
  const textToSend = normalizeMisspellings(text);
  const cacheKey = `${text}_${effectiveSource}_${targetLang}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  try {
    const response = await fetch(getTranslateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text: textToSend,
        targetLang,
        sourceLang: effectiveSource,
      }),
    });

    const data = await response.json();
    const result = data?.translatedText ?? text;
    translationCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.warn("Translation API failed:", error.message);
    translationCache.set(cacheKey, text);
    return text;
  }
};

/**
 * Traduction par lots (pour optimiser les requêtes)
 * Support : Auto-détection de la langue source
 */
export const translateBatch = async (
  texts,
  targetLang = "km",
  sourceLang = "auto",
) => {
  const promises = texts.map((text) =>
    translateTextAPI(text, targetLang, sourceLang),
  );
  return Promise.all(promises);
};

/**
 * Vider le cache de traduction
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

/**
 * Obtenir la taille du cache
 */
export const getCacheSize = () => {
  return translationCache.size;
};
